import type { SlotValues } from "./catalogue/types";
import { capitalize } from "./catalogue/wording";
import { WEEKDAYS, Weekday } from "./dateRange";
import { roleIn, TEAM_ROLES } from "../team/teamRoles";
import { FOR_THE_ASKER, isFiller, trimFiller } from "./fillerWords";

/** The days a task can be for: today, tomorrow, the coming weekday, or a date ("date:11-28" is 28 November). */
export type DayKey = "today" | "tomorrow" | Weekday | `date:${string}`;

const DAY_WORDS = new RegExp(`\\b(today|tomorrow|${WEEKDAYS.join("|")})\\b`, "i");

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
export const MONTH = "(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\b";
const DAY_OF_MONTH = "(\\d{1,2})(?:st|nd|rd|th)?";
/** "28 November", "the 3rd of Dec", "November 28th", or a date already read ("date:11-28") */
const DATE_FORMS = [
  new RegExp(`\\b${DAY_OF_MONTH}\\s+(?:of\\s+)?${MONTH}`, "i"),
  new RegExp(`\\b${MONTH}\\s+${DAY_OF_MONTH}\\b`, "i"),
];
const FULL_DATE_KEY = /\bdate:(\d{4})-(\d{2})-(\d{2})\b/;
const DATE_KEY = /\bdate:(\d{2})-(\d{2})\b/;

/** "Thursday 29 October", "Friday, 23 October": a weekday said before its date is part of the same day. */
const WEEKDAY_BEFORE = `(?:(?:${WEEKDAYS.join("|")})\\s*,?\\s+)?`;
/** "29 October 2026": the year after a date is part of it, never left in a title. */
const YEAR_AFTER = "(?:,?\\s+\\d{4}\\b)?";

/** A day phrase anywhere in a message ("on Friday", "28 November"), for stripping it out of what's left. */
export const DAY_PHRASE = new RegExp(
  `(?:\\bon\\s+\\d{1,2}/\\d{1,2}(?:/\\d{4})?\\b|^\\s*\\d{1,2}/\\d{1,2}(?:/\\d{4})?\\s*$|\\s*\\b(?:on\\s+|this\\s+|next\\s+)?(?:${WEEKDAY_BEFORE}(?:the\\s+)?${DAY_OF_MONTH}\\s+(?:of\\s+)?${MONTH}${YEAR_AFTER}|${WEEKDAY_BEFORE}${MONTH}\\s+${DAY_OF_MONTH}\\b${YEAR_AFTER}|(?:today|tomorrow|${WEEKDAYS.join("|")})\\b(?!'s)))`,
  "gi"
);

/** How a weekday is shortened: "thurs", "fri", "wed". */
const SHORT_WEEKDAYS: Record<string, Weekday> = {
  mon: "monday",
  tue: "tuesday",
  tues: "tuesday",
  wed: "wednesday",
  weds: "wednesday",
  thu: "thursday",
  thur: "thursday",
  thurs: "thursday",
  fri: "friday",
  sat: "saturday",
  sun: "sunday",
};
/**
 * "nxt thurs", "next fri", "on wed": a shortened weekday, only after a word that says it's a day,
 * so "the sun" and "I sat" stay as they are.
 */
const SHORT_WEEKDAY = new RegExp(`\\b(next|nxt|this|on|every|coming|by|until|till)\\s+(${Object.keys(SHORT_WEEKDAYS).join("|")})\\b\\.?`, "gi");

/** The day words as the rest of this file reads them: "nxt thurs" is "next thursday", "tmrw" is "tomorrow". */
export function spelledOutDays(text: string): string {
  return text
    .replace(SHORT_WEEKDAY, (_, before: string, day: string) => `${before.toLowerCase() === "nxt" ? "next" : before} ${SHORT_WEEKDAYS[day.toLowerCase()]}`)
    .replace(/\bnxt\b/gi, "next")
    .replace(/\b(?:tmrw|tmrws|tmr|tomoz|tommorow|tomorow|tommorrow)\b/gi, "tomorrow");
}

/** A time of day, "at 6pm" or "10:30 am": left for the event screen. */
const TIME = /\s*\b(?:at\s+)?\d{1,2}(?::\d{2})?\s*(?:am|pm)\b/gi;

/**
 * What a Notice says: after "about", "reminding parents (that)", "telling everyone",
 * "asking parents", "to let parents know", or a colon ("Make an urgent notice: …").
 */
const TOPIC =
  /\s*(?::\s+|\b(?:about|regarding|re:?)\s+|\b(?:reminding|telling|asking|saying|to\s+(?:remind|tell|ask|let))\s+(?:(?:the\s+)?(?:parents|families|everyone|everybody|people|them|staff|learners)\s+)?(?:(?:know|that|to|about)\s+)?)(.+)$/i;

/** "…every Tuesday, to TEST Grade 4 2027": who it's for, said after the message it gives. */
const MESSAGE_THEN_WHO = /,?\s+(?:to|for)\s+((?:the\s+)?(?:[\w'’-]+\s+){0,4}[\w'’-]+)[\s.!]*$/i;

/** Words that say what kind of thing is being made, or where, so they're never a Circle or User. */
const TASK_WORDS = /\b(notice|message|announcement|reminder|template|draft|calendar)s?\b/i;

/** Says an event is for the Internal team only; taken out of the message so it isn't read as a Circle. */
export const STAFF_ONLY =
  /\s*\b(?:(?:staff|teachers)[- ]only|internal team only|(?:just |only )?for (?:the )?(?:internal )?(?:team|staff)(?!\s+meetings?\b)(?: only)?|only for staff)\b/i;
/** Also for the Internal team only, but left in the message as what's being made. */
const STAFF_EVENT = /\bstaff meetings?\b/i;

/** An email address or phone number, with the "on" or "at" before it: who an Invite goes to. */
const CONTACT = /\s*(?:\b(?:on|at)\s+)?([^\s@]+@[^\s@]+\.[^\s@?!,]+|\+?\d[\d\s()-]{6,}\d)/;

/** "DM Thandi's mom", "direct message the Grade 2 rep": a Notice to one User, said as a direct message rather than "to/for". */
const DIRECT_MESSAGE = /^(?:dm|direct[- ]message|private[- ]message)\s+(.+)$/i;

/** Phone numbers have at least 9 digits, so a date like 2026-10-03 isn't one. */
const isContact = (found: string) => found.includes("@") || found.replace(/\D/g, "").length >= 9;

/**
 * Nowhere in particular: "remove Jane from the app" names no Circle or Team to
 * tell apart from leaving the Organization, so it still names who's removed.
 */
const GENERIC_FROM = /^(?:the\s+)?(?:app|noticeboard|dashboard|platform|system)$/i;

/** Words that say "some User" rather than name one: "add a parent to Grade 2" is an Invite. */
const NOT_A_USER =
  /^(?:a|an|the|some|someone|somebody|new|this|that|my|our|all|them|everyone|people|users?|parents?|members?|families|family)\b/i;

/** The role someone is added to the Team as: "as a Creator". Left in the message, as it says the task. */
const ROLE = new RegExp(`\\bas\\s+(?:an?\\s+)?(?:${TEAM_ROLES.join("|")})\\b`, "i");

/** "to the team", "to the staff": the Team, not a Circle. */
const THE_TEAM = /^(?:the |our |my )?(?:team|staff)$/i;

/** "want to", "going to", "need to": the infinitive "to" before a verb, never who it's for. */
const INFINITIVE_TO =
  /\b(?:want(?:s|ed)?|need(?:s|ed)?|like[sd]?|lov(?:e|es|ed)|hop(?:e|es|ed|ing)|wish(?:es|ed)?|try(?:ing)?|plan(?:s|ning|ned)?|going|able)\s*$/i;

/** "a reminder", "the reminder": Send reminder on a Notice already out, never a new Notice. */
const REMINDER = /\b(?:a|the|another|this)\s+reminder\b/i;

/**
 * Starting an Award: "make Gold certificates for the Maths Olympiad winners".
 * Groups: up to the verb, the level ("Gold"), "certificates", and what follows.
 */
const AWARD_TASK =
  /^(.*?\b(?:make|create|start|set\s+up)\s+)(?:(?:a|an|some|the|new|our)\s+)*(.*?)\s*\b(certificates?|awards?)\b(?!\s+templates?)(.*)$/i;
/** Who an Award is for, as the occasion: "the Maths Olympiad winners" is the Maths Olympiad. */
const AWARD_FOR = /^\s*(?:for|to)\s+(?:the\s+|our\s+)?(.+?)(?:\s+(?:winners?|children|kids|learners|pupils|students))?$/i;
const AWARD_CALLED = /^\s*(?:called|named)\s+(.+)$/i;

/**
 * The Award a message starts, named the way Awards are ("Maths Olympiad – Gold"),
 * and the message without it ("make certificates"). Null when it doesn't start one.
 */
function findAward(text: string): { award?: string; rest: string } | null {
  const found = text.match(AWARD_TASK);
  if (!found) return null;
  const [, lead, level, noun, after] = found;
  // "Gold" or "merit", never "a notice for the" or "an event to hand out the": that's another task about Awards
  if (TASK_WORDS.test(level) || /\b(?:events?|for|to|about|out)\b/i.test(level)) return null;
  const called = after.match(AWARD_CALLED)?.[1];
  const occasion = after.match(AWARD_FOR)?.[1];
  const capitalLevel = level && capitalize(level);
  const rawAward = called ?? (occasion && capitalLevel ? `${occasion} – ${capitalLevel}` : occasion || capitalLevel);
  // Taken word for word from the message, which may not start with a capital letter.
  const award = rawAward && capitalize(rawAward);
  return { ...(award ? { award: tidy(award) } : {}), rest: `${lead}${noun}` };
}

/** "the last draft" and the like: the newest one. */
export const LATEST = "latest";
const LATEST_WORDS = new Set(["last", "latest", "newest", "recent", "most recent"]);
const GENERIC_NAMES = new Set(["a", "an", "new", "one", "same", "other", "that", "this"]);

const pad = (n: number) => String(n).padStart(2, "0");

/** The date a message mentions, as a day key ("date:11-28"), if any. */
function findDate(text: string, now: Date = new Date()): DayKey | null {
  // Match the wizard's DD/MM/YYYY format. A bare answer or an explicit "on" avoids fractions in prose.
  const numeric = text.trim().match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?[.!?]*$/)
    ?? text.match(/\bon\s+(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?(?=$|[\s,.!?])/i);
  if (numeric) {
    const day = Number(numeric[1]);
    const month = Number(numeric[2]);
    const year = numeric[3] ? Number(numeric[3]) : now.getFullYear();
    const check = new Date(year, month - 1, day);
    if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) return null;
    return `date:${numeric[3] ? `${year}-` : ""}${pad(month)}-${pad(day)}`;
  }
  const full = text.match(FULL_DATE_KEY);
  if (full) return full[0] as DayKey;
  const key = text.match(DATE_KEY);
  if (key) return `date:${key[1]}-${key[2]}`;
  const [dayFirst, monthFirst] = DATE_FORMS.map((form) => text.match(form));
  const [day, month] = dayFirst ? [dayFirst[1], dayFirst[2]] : monthFirst ? [monthFirst[2], monthFirst[1]] : [];
  if (!day || !month) return null;
  const monthNumber = MONTHS.indexOf(month.slice(0, 3).toLowerCase()) + 1;
  const dayNumber = Number(day);
  const found = dayFirst ?? monthFirst;
  const year = found ? text.slice((found.index ?? 0) + found[0].length).match(/^,?\s+(\d{4})\b/)?.[1] : undefined;
  return dayNumber >= 1 && dayNumber <= 31 ? `date:${year ? `${year}-` : ""}${pad(monthNumber)}-${pad(dayNumber)}` : null;
}

const NEXT_WEEKDAY = new RegExp(`\\bnext\\s+(${WEEKDAYS.join("|")})\\b`, "i");

/**
 * The day a message mentions, if any ("Friday's trip" is for Friday, "28 November" for that date).
 * "Next Friday" said on a Friday is a week from today, never today.
 */
export function findDay(said: string, now: Date = new Date()): DayKey | null {
  const text = spelledOutDays(said);
  const date = findDate(text, now);
  if (date) return date;
  if (/(?:^|\bon\s+)\d{1,2}\/\d{1,2}(?:\/\d{4})?/.test(text)) return null;
  const next = text.match(NEXT_WEEKDAY);
  if (next && WEEKDAYS.indexOf(next[1].toLowerCase() as Weekday) === now.getDay()) {
    const weekOn = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
    return `date:${pad(weekOn.getMonth() + 1)}-${pad(weekOn.getDate())}`;
  }
  const found = text.match(DAY_WORDS);
  if (found) return found[1].toLowerCase() as DayKey;
  // "on the 5th": that day of this month, or of next month once it has passed
  const ordinal = text.match(/\b(?:on\s+)?the\s+(\d{1,2})(?:st|nd|rd|th)\b/i);
  const dayNumber = ordinal ? Number(ordinal[1]) : 0;
  if (dayNumber >= 1 && dayNumber <= 31) {
    const month = dayNumber >= now.getDate() ? now.getMonth() : now.getMonth() + 1;
    const date = new Date(now.getFullYear(), month, dayNumber);
    return `date:${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
  return null;
}

/**
 * That day's date: a weekday is the coming one, or today if it's that day now;
 * a date is this year's, or next year's once it has passed.
 */
export function resolveDay(key: DayKey, now: Date = new Date()): Date {
  const full = key.match(FULL_DATE_KEY);
  if (full) return new Date(Number(full[1]), Number(full[2]) - 1, Number(full[3]));
  const date = key.match(DATE_KEY);
  if (date) {
    const [month, day] = [Number(date[1]) - 1, Number(date[2])];
    const thisYear = new Date(now.getFullYear(), month, day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return thisYear < today ? new Date(now.getFullYear() + 1, month, day) : thisYear;
  }
  const offset =
    key === "today"
      ? 0
      : key === "tomorrow"
        ? 1
        : (WEEKDAYS.indexOf(key as Weekday) - now.getDay() + 7) % 7;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
}

/** How far back a date said can be and still mean this year's, a day that has passed, rather than next year's. */
const PASSED_WITHIN_MONTHS = 6;

/**
 * The day as it was meant: as resolveDay, except a date earlier this year, in the
 * last six months, is this year's ("1 October" on 3 October has passed).
 */
export function dayAsSaid(key: DayKey, now: Date = new Date()): Date {
  if (FULL_DATE_KEY.test(key)) return resolveDay(key, now);
  const resolved = resolveDay(key, now);
  const thisYear = new Date(now.getFullYear(), resolved.getMonth(), resolved.getDate());
  const since = new Date(now.getFullYear(), now.getMonth() - PASSED_WITHIN_MONTHS, now.getDate());
  return thisYear < resolved && thisYear >= since ? thisYear : resolved;
}


export const tidy = (text: string) => text.replace(/\s+/g, " ").trim();
const withoutEndPunctuation = (text: string) => text.trim().replace(/[\s?.!]+$/, "");

/**
 * The one day an answer gives, and the answer without it: "Sports day on Friday" is
 * Sports day, on Friday. Only a day said once and plainly ("on Friday", "next Monday",
 * "28 November", or a day ending the answer), so "Monday Club meeting" gives none.
 */
export function dayGivenIn(said: string, now: Date = new Date()): { day: DayKey; rest: string } | null {
  const text = spelledOutDays(said);
  const found = Array.from(text.matchAll(DAY_PHRASE));
  const days = new Set(found.map((m) => findDay(m[0], now)));
  if (found.length === 0 || days.size !== 1 || days.has(null)) return null;
  const plain = found.some(
    (m) =>
      /^\s*(?:on|this|next)\s/i.test(m[0]) ||
      findDate(m[0], now) ||
      !text.slice((m.index ?? 0) + m[0].length).replace(/[\s?.!,]+$/, "")
  );
  return plain ? { day: findDay(found[0][0], now) as DayKey, rest: tidy(text.replace(DAY_PHRASE, " ")) } : null;
}

/** "the Sports Day template", "my trip draft", "the template called Sports Day" */
function findNamed(text: string): { slot: "template" | "draft"; name: string } | null {
  const before = text.match(/\b(?:the|my|our)\s+(.+?)\s+(template|draft)\b/i);
  if (before) return { slot: before[2].toLowerCase() as "template" | "draft", name: before[1] };
  const after = text.match(/\b(template|draft)\s+(?:called|named)\s+(.+)$/i);
  if (after) return { slot: after[1].toLowerCase() as "template" | "draft", name: after[2] };
  return null;
}

/**
 * "continue the last one", "open the latest one": a Draft meant without the word, next to a verb
 * that's only ever about carrying one on ("Continue my last draft" said without "draft").
 */
const CONTINUE_LATEST_DRAFT =
  /^(?:continue|open|finish|edit|resume|go back to|carry on with|keep working on)\s+(?:my|our|the)\s+(?:last|latest|newest|most recent)\s+one\b/i;

/**
 * Reads a task's details as the person wrote them: who it's for, what it's
 * about, the day, an email address or phone number, a User being added to a Circle or someone to the Team (and their role), a named Template or Draft, an Award being started, and whether it's for the Internal team only. `rest` is the message
 * without them ("send a notice"), which is what gets matched to a task.
 */
export function readTaskDetails(text: string): { said: SlotValues; rest: string } {
  const said: SlotValues = {};
  let rest = withoutEndPunctuation(text);

  // Move an audience after a named title ahead of it, so title and body remain separate.
  rest = rest.replace(/\b(notice|announcement)\s+(?:called|named)\s+(.+?)\s+(to|for)\s+(.+?)\s+saying\s+(.+)$/i, "$1 $3 $4 titled $2 saying $5")
    .replace(/\b(notice|announcement)\s+(?:called|named)\s+/i, "$1 titled ");
  const contact = rest.match(CONTACT);
  if (contact && contact.index !== undefined && isContact(contact[1])) {
    said.contact = contact[1];
    rest = rest.slice(0, contact.index) + rest.slice(contact.index + contact[0].length);
  }

  // "DM Thandi's mom": who it's for, named directly rather than after "to" or "for"
  const directMessage = rest.match(DIRECT_MESSAGE);
  if (directMessage) {
    said.audience = tidy(directMessage[1].replace(/[\s?.!]+$/, ""));
    rest = "send a direct message";
  }

  const day = findDay(rest);
  if (day) said.day = day;

  // "a notice titled Hello": the title, said as a name rather than after "about"
  // "titled Sports Kit Reminder saying Learners need…, to Grade 4": the title, then what it says, then who it's for
  const titled = rest.match(/\btitled\s+(.+?)(?:\s+(?:saying|that says|which says|with (?:the )?message)\s+(.+?))?(?:\s+about\b.*)?$/i);
  if (titled && titled.index !== undefined) {
    const name = tidy(titled[1].replace(/^["'“”]+|["'“”]+$/g, ""));
    if (name && !isFiller(name)) said.topic = name;
    if (titled[2]) {
      const candidate = titled[2].match(MESSAGE_THEN_WHO);
      // A time range or weekday continuation is message content, never a recipient.
      const temporal = /^(?:\d|today\b|tomorrow\b|(?:next|this|last)\s+|(?:mon|tues?|wednes|thurs?|fri|satur|sun)days?\b)/i;
      const sentTo = candidate && !temporal.test(candidate[1]) ? candidate : null;
      if (sentTo?.index !== undefined) said.audience = tidy(sentTo[1]);
      const body = sentTo?.index !== undefined ? titled[2].slice(0, sentTo.index) : titled[2];
      const originalAt = !sentTo ? text.lastIndexOf(body) : -1;
      said.message = tidy(originalAt >= 0 ? text.slice(originalAt) : body).replace(/^["“]|["”]$/g, "");
    }
    rest = rest.slice(0, titled.index);
  }

  const topic = rest.match(TOPIC);
  if (topic && topic.index !== undefined) {
    // "about quickly" says nothing about what it's about
    if (!isFiller(topic[1])) said.topic = tidy(trimFiller(topic[1]));
    // "saying Happy Diwali from Grade 4!": the words to send, as well as what it's about
    if (said.topic && /^\s*saying\b/i.test(topic[0])) {
      // With its closing "!" or "." as typed, which the topic leaves off
      const at = text.lastIndexOf(topic[1]);
      said.message = at >= 0 ? tidy(text.slice(at)) : said.topic;
    }
    rest = rest.slice(0, topic.index);
  }
  rest = rest.replace(FOR_THE_ASKER, "");

  // A Reminder, a Poll or an Urgent notice, said outside what it's about
  if (REMINDER.test(rest) || /\bremind(?:s|ed|ing)?\b/i.test(rest)) said.noticeType = "reminder";
  else if (/\bpolls?\b/i.test(rest)) said.noticeType = "poll";
  else if (/\burgent\b|\bemergency\s+notice\b/i.test(rest)) said.noticeType = "urgent";

  const staffOnly = rest.match(STAFF_ONLY);
  if (staffOnly || STAFF_EVENT.test(rest)) said.staffOnly = true;
  if (staffOnly) rest = rest.replace(STAFF_ONLY, "");

  const award = findAward(rest);
  if (award) {
    if (award.award) said.award = award.award;
    rest = award.rest;
  }

  const named = findNamed(rest);
  if (named) {
    const lowerName = named.name.toLowerCase();
    if (named.slot === "draft" && LATEST_WORDS.has(lowerName)) {
      said.draft = LATEST;
    } else if (!GENERIC_NAMES.has(lowerName)) {
      said[named.slot] = tidy(named.name);
    }
    rest = rest.replace(named.name, "");
  } else if (CONTINUE_LATEST_DRAFT.test(rest)) {
    said.draft = LATEST;
  }

  // Who it's from: "approve the join requests from Grade R"
  const from = rest.match(/\s*\bfrom\s+(.+?)(?=\s+\b(?:to|for|on|by|at)\b|$)/i);
  if (from && from.index !== undefined) {
    const whoFrom = tidy(from[1].replace(DAY_PHRASE, ""));
    if (!GENERIC_FROM.test(whoFrom)) said.from = whoFrom;
    rest = rest.slice(0, from.index) + rest.slice(from.index + from[0].length);
  }

  const role = rest.match(ROLE);
  if (role) said.role = roleIn(role[0]) ?? undefined;

  // The last "to" or "for" says who it's for: "I want to send a notice to Grade 2".
  // An infinitive "to" ("want to invite parents") is never that, even when it's the only one.
  let toTheTeam = false;
  const isInfinitiveTo = (match: RegExpMatchArray) =>
    /^to\s/i.test(match[0]) && match.index !== undefined && INFINITIVE_TO.test(rest.slice(0, match.index));
  const toOrFor = Array.from(rest.matchAll(/\b(?:to|for)\s+/gi))
    .filter((match) => !isInfinitiveTo(match))
    .pop();
  if (toOrFor?.index !== undefined) {
    const after = rest.slice(toOrFor.index + toOrFor[0].length);
    const audience = tidy(
      trimFiller(after.split(/\s+\b(?:on|by|at|as)\b/i)[0].replace(DAY_PHRASE, "").replace(TIME, ""))
    );
    toTheTeam = THE_TEAM.test(audience);
    const sameAsBefore = /^(?:the\s+)?same(?:\s+(?:thing|one|event|notice|again))?$/i.test(audience);
    if (audience && !isFiller(audience) && !toTheTeam && !sameAsBefore && !TASK_WORDS.test(audience)) {
      said.audience = audience;
      rest = rest.slice(0, toOrFor.index) + after.slice(after.indexOf(audience) + audience.length);
    }
  }

  // Who is being added to the Team ("add Lebo as a Creator") or to that Circle ("add Jane to Youth")
  const toTeam = Boolean(said.role) || toTheTeam;
  const adding =
    (toTeam || said.audience) &&
    rest.match(toTeam ? /^(.*?\b(?:add|invite)\s+)(.+?)((?:\s+(?:to|as)\b.*)?)$/i : /^(.*?\badd\s+)(.+)()$/i);
  if (adding && !NOT_A_USER.test(adding[2]) && !TASK_WORDS.test(adding[2]) && !/\bevents?\b/i.test(adding[2])) {
    // Left as "add to the team" rather than "add someone", which is adding to a Circle or an Invite
    said[toTeam ? "member" : "user"] = tidy(adding[2]);
    rest = toTeam ? `${adding[1]}${adding[3]}` : `${adding[1]}someone`;
  }

  // Who is being removed: "please remove Jane Dube". Not "from" a Circle, a Notice or an Event.
  const removing = !said.from && !said.audience && rest.match(/^(.*?\b(?:remove|delete)\s+)(.+)$/i);
  if (removing && !NOT_A_USER.test(removing[2]) && !TASK_WORDS.test(removing[2]) && !/\bevents?\b/i.test(removing[2])) {
    said.user = tidy(removing[2].replace(/[\s,.!?]+$/, ""));
    rest = `${removing[1]}someone`;
  }

  return { said, rest: tidy(rest.replace(DAY_PHRASE, "").replace(TIME, "")) };
}
